import React from 'react'
import { useTranslation } from 'react-i18next'

import { useHighlightCells } from '~/hooks/useHighlightCells'
import { useTamRatio } from '~/hooks/useTamRatio'
import { type Group, type RowInfo } from '~/utils'

import {
  useAtomValueContext,
  useSetAtomContext,
} from '../providers/AtomsProvider'
import {
  buildIndividualTableInfo,
  type IndividualColumnKey,
  type RowData,
} from '../utils'

import { useGroupAndAllNameMap } from './useGroupAndAllNameMap'
import { useSortFunc } from './useSortFunc'
import { useTabulatedGroupsAndAll } from './useTabulatedGroupsAndAll'
import { useTotalData } from './useTotalData'

type IndividualTableInfoStyles = {
  answerText: string
}
type CreateIndividualTableInfoParams<T extends RowData<string>> = {
  getAnswerText: (rowData: T) => string
  getRowId: (rowData: T) => string
  styles: IndividualTableInfoStyles
}

export const useIndividualTableInfo = <T extends RowData<string>>(
  rowsData: T[],
  { getAnswerText, getRowId, styles }: CreateIndividualTableInfoParams<T>,
): RowInfo<IndividualColumnKey>[] => {
  const { t } = useTranslation()
  const groupAndAllNameMap = useGroupAndAllNameMap()
  const setNameEditingGroup = useSetAtomContext('nameEditingGroup')
  const totalData = useTotalData()
  const tamRatio = useTamRatio()
  const dataDisplayType = useAtomValueContext('dataDisplayType')
  const { addHighlightProps } = useHighlightCells('individual')
  const hiddenRowIds = useAtomValueContext('hiddenRowIds')
  const sortFunc = useSortFunc('individualSortValue')
  const sortValue = useAtomValueContext('individualSortValue')

  const tabulatedGroupsAndAll = useTabulatedGroupsAndAll()
  const groupsDiffs = useAtomValueContext('groupsDiffs')

  const handleGroupNameClick = React.useCallback(
    (group: string) => {
      setNameEditingGroup(group as Group)
    },
    [setNameEditingGroup],
  )

  return React.useMemo(
    () =>
      buildIndividualTableInfo({
        addHighlightProps,
        answerTextClassName: styles.answerText,
        dataDisplayType,
        getAnswerText,
        getRowId,
        groupAndAllNameMap,
        groups: tabulatedGroupsAndAll,
        groupsDiffs,
        hiddenRowIds,
        onGroupNameClick: handleGroupNameClick,
        rowsData,
        sortFunc,
        sortValue,
        t,
        tamRatio,
        totalData: { counts: totalData.counts, ratios: totalData.ratios },
      }),
    [
      addHighlightProps,
      dataDisplayType,
      getAnswerText,
      getRowId,
      groupAndAllNameMap,
      groupsDiffs,
      handleGroupNameClick,
      hiddenRowIds,
      rowsData,
      sortFunc,
      sortValue,
      styles.answerText,
      t,
      tabulatedGroupsAndAll,
      tamRatio,
      totalData.counts,
      totalData.ratios,
    ],
  )
}
